import { debugLog } from './debugLog';
/**
 * Government API Client for data.gov.il
 * Fetches performance and exposure data for investment tracks.
 */

const RESOURCE_IDS = {
  GEMEL:     "a30dcbea-a1d2-482c-ae29-8f781f5025fb", // קופות גמל, גמל להשקעה, השתלמות
  PENSION:   "6d47d6b5-cb08-488b-b333-f1e717b1e1bd", // קרנות פנסיה
  INSURANCE: "c6c62cc7-fe02-4b18-8f3e-813abfbb4647", // פוליסות ביטוח מנהלים
};

// Mapping of XML KOD-MEZAHE-YATZRAN (legal ID) → CMA PARENT_COMPANY_ID
// Used to construct candidate FUND_IDs for the InsuranceNet fallback.
// FUND_ID in InsuranceNet = PARENT_COMPANY_ID + "0" + track number.
const LEGAL_ID_TO_CMA_COMPANY = {
  "520024647": ["14"],              // כלל חברה לביטוח
  "520004078": ["5", "17"],         // מגדל חברה לביטוח
  "520004896": ["17", "5"],         // מגדל (קוד נוסף)
  "520007568": ["4", "16"],         // הראל חברה לביטוח
  "520017799": ["12", "18"],        // הפניקס חברה לביטוח
  "520029364": ["15", "19"],        // מנורה מבטחים
  "520039454": ["11"],              // כלל פנסיה וגמל
  "520042425": ["8"],               // אלטשולר שחם
  "520000027": ["2"],               // מיטב דש
  "520003542": ["3"],               // אנליסט
  "520034299": ["20"],              // פסגות (לשעבר)
};

// In-memory cache to avoid redundant requests
const cache = new Map();

/**
 * Extracts lookup keys for each maslul to use with the gov API.
 *
 * Gemel/Pension (sugMutzar 2,3,4,9):
 *   Returns the last-7-digits of KOD-MASLUL-HASHKAA as a numeric string.
 *   This is the FUND_ID in the GemelNet/PensiaNet dataset.
 *
 * Insurance (sugMutzar 5):
 *   KOD-MASLUL-HASHKAA is a 30-char composite: company(9) + policy(12) + track(9).
 *   The InsuranceNet FUND_ID uses the CMA's own company code (not the XML tax ID),
 *   so we cannot construct it directly.  Instead we return a compound key
 *   "LEGAL_ID:TRACK_NUM" (e.g. "520004078:12") so the fetcher can search by
 *   legal ID via `q` and match by the track number suffix.
 */
// History snapshots keep only {name, balance, tsua} per maslul, but our
// parsers append the track number to the display name ("... - 1689").
// Recover it from there when code/trackId are missing.
export function trackNumFromName(name) {
  const m = String(name || "").match(/-\s*(\d{1,7})\s*$/);
  return m ? parseInt(m[1], 10) : NaN;
}

export function extractFundIds(maslulim, sugMutzar, companyCode) {
  return (maslulim || []).map(m => {
    const code = String(m.code || "");
    if (String(sugMutzar) === "1" || String(sugMutzar) === "5") {
      // Insurance: KOD-MASLUL-HASHKAA = company(9) + internal(12) + track(9) = 30 chars
      // The 9-digit track field may have prefix padding (e.g. 400017012 → real ID is 17012).
      // Use last 7 digits (same as parseMaslulim) for consistent extraction.
      let trackNum = code.length >= 7
        ? parseInt(code.slice(-7), 10)
        : parseInt(m.trackId || code, 10);
      if (isNaN(trackNum) || trackNum === 0) trackNum = trackNumFromName(m.name);
      if (!isNaN(trackNum) && trackNum !== 0)
        return `${companyCode}:${trackNum}`;
      return "";
    }
    // Gemel / Pension: last 7 digits of KOD-MASLUL-HASHKAA
    if (code.length >= 7) {
      const cleaned = String(parseInt(code.slice(-7), 10));
      if (!isNaN(parseInt(cleaned)) && cleaned !== "0") return cleaned;
    }
    if (m.trackId || code) return m.trackId || code;
    const fromName = trackNumFromName(m.name);
    return isNaN(fromName) ? "" : String(fromName);
  }).filter(v => v && v !== "" && v !== "0");
}

/**
 * Fetches performance data for a list of fund IDs.
 * @param {string[]} fundIds - FUND_ID strings (gemel/pension) OR "LEGAL_ID:TRACK" compounds (insurance)
 * @param {string|number} sugMutzar - SUG-MUTZAR code (2/3/4/5/9)
 * @returns {Promise<Object>} Map of id→record. Gemel/pension keyed by FUND_ID; insurance keyed by track number.
 */
export async function fetchTrackPerformance(fundIds, sugMutzar) {
  if (!fundIds || fundIds.length === 0) return {};

  const resourceId = getResourceId(sugMutzar);
  if (!resourceId) return {};

  const validIds = [...new Set(fundIds.filter(id => id && id.trim() !== ""))];
  if (validIds.length === 0) return {};

  const cacheKey = `${resourceId}_${validIds.sort().join(",")}`;
  if (cache.has(cacheKey)) return cache.get(cacheKey);

  try {
    const results = (String(sugMutzar) === "1" || String(sugMutzar) === "5")
      ? await fetchInsuranceByLegalId(validIds, resourceId)
      : await fetchByFundId(validIds, resourceId, sugMutzar);

    cache.set(cacheKey, results);
    return results;
  } catch (err) {
    console.error("[govApi] Failed to fetch performance data:", err);
    return {};
  }
}

// ── Gemel / Pension: filter by FUND_ID ───────────────────────────────────────

async function fetchByFundId(validIds, resourceId, sugMutzar) {
  const ids = validIds.map(id => parseInt(id, 10));
  const filterObj = { FUND_ID: ids.length === 1 ? ids[0] : ids };
  const params = new URLSearchParams({
    resource_id: resourceId,
    filters: JSON.stringify(filterObj),
    sort: "REPORT_PERIOD desc",
    limit: "100",
  });

  const proxyUrl  = `/api/gov-il/search?${params.toString()}`;
  const directUrl = `https://data.gov.il/api/3/action/datastore_search?${params.toString()}`;

  debugLog(`[govApi] Fetching ${ids.length} tracks for SUG-MUTZAR ${sugMutzar}...`);

  let response = await fetch(proxyUrl).catch(() => null);
  if (!response || !response.ok) {
    console.warn("[govApi] Proxy failed, trying direct fetch...");
    response = await fetch(directUrl);
  }
  if (!response.ok) {
    const txt = await response.text();
    console.error(`[govApi] Both fetch strategies failed: ${response.status}`, txt);
    throw new Error(`API error: ${response.status}`);
  }

  const data = await response.json();
  if (!data.success) throw new Error("API success=false");
  let records = data.result.records || [];

  // Fallback: q search if FUND_ID filter returns nothing
  if (records.length === 0 && ids.length > 0) {
    const qParams = new URLSearchParams({ resource_id: resourceId, q: ids[0].toString(), limit: "50" });
    const qRes = await fetch(`/api/gov-il/search?${qParams.toString()}`).catch(() => null);
    if (qRes?.ok) {
      const qData = await qRes.json();
      if (qData.success && qData.result.records?.length > 0) records = qData.result.records;
    }
  }

  const results = {};
  [...records]
    .sort((a, b) => (b.REPORT_PERIOD || 0) - (a.REPORT_PERIOD || 0))
    .forEach(record => {
      const id = String(parseInt(record.FUND_ID, 10));
      if (!results[id]) results[id] = record;
    });
  return results;
}

// ── Insurance: search by company legal ID, match by track number suffix ───────

async function fetchInsuranceByLegalId(compoundIds, resourceId) {
  // compoundIds: ["520004078:12", "520004078:88", ...]
  // Group by company legal ID (policies from multiple companies may appear together)
  const byCompany = {};
  for (const cid of compoundIds) {
    const sep = cid.indexOf(":");
    const legalId  = cid.slice(0, sep);
    const trackNum = cid.slice(sep + 1);
    if (!byCompany[legalId]) byCompany[legalId] = new Set();
    byCompany[legalId].add(trackNum);
  }

  const allResults = {};

  await Promise.all(Object.entries(byCompany).map(async ([legalId, trackNumsSet]) => {
    const trackNums = [...trackNumsSet];
    debugLog(`[govApi] Fetching insurance tracks [${trackNums.join(",")}] for company ${legalId}...`);

    const params = new URLSearchParams({
      resource_id: resourceId,
      q: legalId,           // full-text search — finds records where legal ID appears
      sort: "REPORT_PERIOD desc",
      limit: "100",
    });

    const proxyUrl  = `/api/gov-il/search?${params.toString()}`;
    const directUrl = `https://data.gov.il/api/3/action/datastore_search?${params.toString()}`;

    let response = await fetch(proxyUrl).catch(() => null);
    if (!response?.ok) response = await fetch(directUrl).catch(() => null);
    if (!response?.ok) return;

    const data = await response.json().catch(() => null);
    if (!data?.success) return;

    const records = data.result.records || [];

    debugLog(`[govApi] Insurance company ${legalId}: got ${records.length} records, sample FUND_IDs:`,
      records.slice(0, 5).map(r => r.FUND_ID));

    // Latest record per FUND_ID (API may return multiple periods)
    const latestByFundId = {};
    for (const rec of records) {
      const fid = String(parseInt(rec.FUND_ID, 10));
      if (!latestByFundId[fid] || (rec.REPORT_PERIOD || 0) > (latestByFundId[fid].REPORT_PERIOD || 0))
        latestByFundId[fid] = rec;
    }

    // FUND_ID = CMA company code (3-5 digits) + track number (no padding).
    // Match by exact equality first, then by suffix (shortest wins to avoid false positives).
    // e.g. trackNum "17012" matches FUND_ID "17012" exactly;
    //      trackNum "12" matches "14012" (shorter) over "140112".
    for (const trackNum of trackNums) {
      if (latestByFundId[trackNum] && !allResults[trackNum]) {
        // Exact match — trackNum IS the full FUND_ID (common for legacy insurance codes)
        allResults[trackNum] = latestByFundId[trackNum];
        continue;
      }
      const matching = Object.keys(latestByFundId)
        .filter(fid => fid.endsWith(trackNum) && fid.length > trackNum.length + 1)
        .sort((a, b) => a.length - b.length);
      if (matching.length > 0 && !allResults[trackNum])
        allResults[trackNum] = latestByFundId[matching[0]];
    }

    // ── Fallback: direct FUND_ID search for unmatched tracks ────────────
    // If suffix-matching failed (e.g. the `q` search by legal ID returned no/wrong
    // records), try constructing FUND_ID candidates and querying the API directly.
    // FUND_ID format in InsuranceNet = PARENT_COMPANY_ID (2-3 digits) + "0" + track number.
    // Common patterns: 14012 (company 14, track 12), 5088 (company 5, track 88).
    const unmatched = trackNums.filter(tn => !allResults[tn] && parseInt(tn, 10) < 1000000);
    if (unmatched.length > 0) {
      // Collect PARENT_COMPANY_IDs from any records we already have
      const companyIds = new Set();
      for (const rec of records) {
        if (rec.PARENT_COMPANY_ID != null)
          companyIds.add(String(parseInt(rec.PARENT_COMPANY_ID, 10)));
      }
      // Also try the known legal-ID → CMA mapping (covers case where `q` returned 0 records)
      const mappedCmas = LEGAL_ID_TO_CMA_COMPANY[legalId];
      if (mappedCmas) {
        if (Array.isArray(mappedCmas)) mappedCmas.forEach(c => companyIds.add(c));
        else companyIds.add(mappedCmas);
      }

      // Build candidate FUND_IDs: trackNum itself, companyId + "0" + trackNum, and companyId + trackNum
      // Filter to sane range — real InsuranceNet FUND_IDs are at most 6 digits
      const candidateFundIds = [];
      for (const tn of unmatched) {
        candidateFundIds.push(parseInt(tn, 10));
        for (const cid of companyIds) {
          candidateFundIds.push(parseInt(`${cid}0${tn}`, 10));
          candidateFundIds.push(parseInt(`${cid}${tn}`, 10));
        }
      }
      const saneCandidates = candidateFundIds.filter(id => !isNaN(id) && id > 0 && id < 1000000);

      if (saneCandidates.length > 0) {
        debugLog(`[govApi] Insurance fallback: trying direct FUND_ID search for [${saneCandidates.join(",")}]...`);
        const fbResult = await fetchByFundIdDirect(saneCandidates, resourceId);
        for (const tn of unmatched) {
          if (allResults[tn]) continue;
          // 1. Check if the track number itself matched (might be a full FUND_ID)
          if (fbResult[tn]) {
            allResults[tn] = fbResult[tn];
            continue;
          }
          // 2. Check candidate combinations
          for (const cid of companyIds) {
            const candidate1 = String(parseInt(`${cid}0${tn}`, 10));
            const candidate2 = String(parseInt(`${cid}${tn}`, 10));
            if (fbResult[candidate1]) { allResults[tn] = fbResult[candidate1]; break; }
            if (fbResult[candidate2]) { allResults[tn] = fbResult[candidate2]; break; }
          }
        }
      }
    }
  }));

  return allResults;
}

// ── Direct FUND_ID filter search (used as fallback for insurance) ─────────────

async function fetchByFundIdDirect(fundIds, resourceId) {
  const ids = [...new Set(fundIds.filter(id => !isNaN(id) && id > 0))];
  if (ids.length === 0) return {};

  const filterObj = { FUND_ID: ids.length === 1 ? ids[0] : ids };
  const params = new URLSearchParams({
    resource_id: resourceId,
    filters: JSON.stringify(filterObj),
    sort: "REPORT_PERIOD desc",
    limit: "100",
  });

  const proxyUrl  = `/api/gov-il/search?${params.toString()}`;
  const directUrl = `https://data.gov.il/api/3/action/datastore_search?${params.toString()}`;

  let response = await fetch(proxyUrl).catch(() => null);
  if (!response?.ok) response = await fetch(directUrl).catch(() => null);
  if (!response?.ok) return {};

  const data = await response.json().catch(() => null);
  if (!data?.success) return {};

  const records = data.result.records || [];
  const results = {};
  for (const rec of records) {
    const fid = String(parseInt(rec.FUND_ID, 10));
    if (!results[fid] || (rec.REPORT_PERIOD || 0) > (results[fid].REPORT_PERIOD || 0))
      results[fid] = rec;
  }
  return results;
}

// ── Prefetch ──────────────────────────────────────────────────────────────────

/**
 * Pre-fetches performance data for all funds, grouped by SUG-MUTZAR.
 * Populates the in-memory cache so subsequent UI lookups are instant.
 * Errors per group are swallowed (best-effort prefetch).
 * @param {Array} funds - List of fund objects (with `sugMutzar` and `maslulim`)
 */
export async function prefetchAllFundsPerformance(funds) {
  if (!funds || funds.length === 0) return;

  // Build one task per fund — same IDs and sugMutzar that FundCard will use later,
  // so the cache keys match and each FundCard tab-click is an instant cache hit.
  const seen = new Set();
  const tasks = [];
  for (const f of funds) {
    if (!f?.sugMutzar || !f?.maslulim?.length) continue;
    if (!getResourceId(f.sugMutzar)) continue;
    const ids = extractFundIds(f.maslulim, f.sugMutzar, f.companyCode);
    if (!ids.length) continue;
    const key = `${f.sugMutzar}_${[...ids].sort().join(",")}`;
    if (seen.has(key)) continue;
    seen.add(key);
    tasks.push({ ids, sugMutzar: f.sugMutzar });
  }

  if (tasks.length === 0) return;
  debugLog(`[govApi] Prefetching performance for ${tasks.length} fund${tasks.length === 1 ? "" : "s"}...`);

  await Promise.all(
    tasks.map(({ ids, sugMutzar }) =>
      fetchTrackPerformance(ids, sugMutzar).catch(err =>
        console.warn(`[govApi] Prefetch failed (SUG-MUTZAR ${sugMutzar}):`, err.message)
      )
    )
  );
  debugLog("[govApi] Prefetch done.");
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function getResourceId(sugMutzar) {
  switch (String(sugMutzar)) {
    case "3": case "4": case "9": return RESOURCE_IDS.GEMEL;
    case "2":                     return RESOURCE_IDS.PENSION;
    case "1": case "5":           return RESOURCE_IDS.INSURANCE;
    default:                      return null;
  }
}
